import type { DefinitionOutput, PluginOutput } from "@magic-md/core";
import type {
  ExecutionCascadeEvent,
  ExecutionCompletedEvent,
  ExecutionFailedEvent,
  ExecutionIterationCompletedEvent,
  ExecutionIterationStartedEvent,
  ExecutionJobChangedEvent,
  ExecutionProgressEvent,
  ExecutionStartedEvent,
} from "@magic-md/engine-client/protocol";
import type { StoreApi } from "zustand";
import { useEditorStore } from "./editor-store";
import type { ExecutionMapSet } from "./execution-entry-builders";
import type { ForIterationEntry } from "./execution-state";
import {
  applyCompletedStateForNonDefinitionUnit,
  applyFailedStateForNonDefinitionUnit,
  applyStartedStateForNonDefinitionUnit,
  isExecutionCancelledMessage,
  phaseLabel,
  runningStarts,
} from "./execution-state-transitions";
import { useLogStore } from "./log-store";
import type { PluginExecutionState } from "./plugin-execution-store";
import {
  cloneExecutionMapForUnitType,
  getOrCreateSessionExecutionSnapshot as getOrCreateSnapshot,
} from "./execution-session-projection";
import { tabSessionRegistry } from "./tab-session-registry";

export type ExecutionProjectionStore = Pick<
  StoreApi<PluginExecutionState>,
  "getState" | "setState"
>;

function extractOutputErrorMessage(output: unknown): string | null {
  if (!output || typeof output !== "object") return null;
  const error = (output as Record<string, unknown>).__error;
  if (!error) return null;
  if (typeof error === "string") return error;
  if (typeof error !== "object") return String(error);
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && message.length > 0
    ? message
    : JSON.stringify(error);
}

export function getDefinitionPluginName(unitId: string): string | undefined {
  return useEditorStore.getState().parsedDocument?.definitions.get(unitId)
    ?.pluginName;
}

export function logCompletedOutputError(unitId: string, output: unknown): void {
  const message = extractOutputErrorMessage(output);
  if (!message) return;
  const pluginName = getDefinitionPluginName(unitId);
  useLogStore.getState().error(`Execution failed: ${unitId} — ${message}`, {
    source: "engine",
    unitId,
    pluginName,
  });
}

export function mergeForIterationEvents(
  previous: readonly ForIterationEntry[],
  events: readonly ExecutionIterationCompletedEvent[],
): ForIterationEntry[] {
  const byIndex = new Map(
    previous.map((iteration) => [iteration.index, iteration]),
  );
  for (const event of events) {
    byIndex.set(event.iteration.index, {
      index: event.iteration.index,
      item: event.iteration.item,
      outputs: event.iteration.outputs ?? {},
      definitions: event.iteration.definitions,
      contentDefinitions: event.iteration.contentDefinitions,
      success: event.iteration.success ?? true,
      error: event.iteration.error,
    });
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

export function applyCascadeRunningTargets(
  source: ExecutionMapSet,
  targetUnitIds: string[],
  startedAt = Date.now(),
): ExecutionMapSet {
  if (targetUnitIds.length === 0) return source;

  const maps: ExecutionMapSet = { ...source };
  const cloneMap = (key: keyof ExecutionMapSet) => {
    if (maps[key] === source[key]) {
      (maps[key] as Map<string, unknown>) = new Map(
        source[key] as Map<string, unknown>,
      );
    }
  };
  const parsedDoc = useEditorStore.getState().parsedDocument;
  for (const targetId of targetUnitIds) {
    runningStarts.set(targetId, startedAt);

    if (
      parsedDoc?.forDefinitions?.has(targetId) ||
      maps.forExecutions.has(targetId)
    ) {
      cloneMap("forExecutions");
      const prev = maps.forExecutions.get(targetId);
      maps.forExecutions.set(targetId, {
        forId: targetId,
        status: "running",
        totalIterations: prev?.totalIterations ?? 0,
        completedIterations: prev?.completedIterations ?? 0,
        iterations: prev?.iterations ?? [],
        startedAt,
        duration: null,
      });
      continue;
    }

    if (
      parsedDoc?.contentDefinitions?.has(targetId) ||
      maps.contentExecutions.has(targetId)
    ) {
      cloneMap("contentExecutions");
      maps.contentExecutions.set(targetId, {
        contentId: targetId,
        status: "running",
        startedAt,
        duration: null,
      });
      continue;
    }

    const parsedImport = parsedDoc?.imports?.find(
      (binding) => binding.namespace === targetId,
    );
    if (parsedImport || maps.importExecutions.has(targetId)) {
      cloneMap("importExecutions");
      const prev = maps.importExecutions.get(targetId);
      maps.importExecutions.set(targetId, {
        importId: targetId,
        status: "running",
        startedAt,
        duration: null,
        ...(prev?.symbols ? { symbols: prev.symbols } : {}),
        ...(parsedImport?.document && prev?.document
          ? { document: prev.document }
          : {}),
        ...(prev?.meta ? { meta: prev.meta } : {}),
      });
      continue;
    }

    if (
      parsedDoc?.uses?.some((binding) => binding.id === targetId) ||
      maps.useExecutions.has(targetId)
    ) {
      cloneMap("useExecutions");
      maps.useExecutions.set(targetId, {
        useId: targetId,
        status: "running",
        startedAt,
        duration: null,
      });
      continue;
    }

    cloneMap("executions");
    const prev = maps.executions.get(targetId);
    maps.executions.set(targetId, {
      unitId: targetId,
      status: "running",
      actionName: prev?.actionName ?? null,
      result: null,
      output: prev?.output ?? null,
      startedAt,
      duration: null,
    });
  }
  return maps;
}

export function hasPendingRunAllForSession(
  store: ExecutionProjectionStore,
  sessionId: string,
): boolean {
  const state = store.getState();
  const ownerTabId = tabSessionRegistry.getTabForSession(sessionId);
  if (ownerTabId && state.runAllPendingTabIds.has(ownerTabId)) return true;
  for (const tabId of state.runAllPendingTabIds) {
    if (tabSessionRegistry.getSessionForTab(tabId) === sessionId) return true;
  }
  return false;
}

/**
 * Project execution notifications for a non-active tab into the
 * session-keyed cache. The active Zustand slice remains untouched until the
 * owning tab becomes active again.
 */
export function routeBackgroundExecution(
  store: ExecutionProjectionStore,
  syncBackgroundRunningTabs: () => void,
  method: string,
  params: unknown,
): void {
  const eventSessionId = (params as { sessionId?: string }).sessionId;
  if (!eventSessionId) return;

  const { sessionExecutionCache } = store.getState();
  const existingSnapshot = sessionExecutionCache.get(eventSessionId);
  const snapshot = existingSnapshot
    ? { ...existingSnapshot }
    : getOrCreateSnapshot(sessionExecutionCache, eventSessionId);
  let changed = false;

  const cloneUnitMap = (unitType: string) => {
    Object.assign(snapshot, cloneExecutionMapForUnitType(snapshot, unitType));
  };

  switch (method) {
    case "event/execution.jobChanged": {
      const event = params as ExecutionJobChangedEvent;
      const keepRunAllPending =
        event.target === "all" &&
        hasPendingRunAllForSession(store, eventSessionId);
      if (event.target === "all") {
        const nextExecutionId =
          event.status === "running" || keepRunAllPending
            ? event.executionId
            : null;
        if (snapshot.activeRunAllExecutionId !== nextExecutionId) {
          snapshot.activeRunAllExecutionId = nextExecutionId;
          changed = true;
        }
      } else if (event.unitId) {
        if (event.status === "running") {
          const currentExecution = snapshot.executions.get(event.unitId);
          const isAuxiliaryAction =
            currentExecution?.status === "running" &&
            !!currentExecution.actionName &&
            !!event.action &&
            currentExecution.actionName !== event.action;
          if (
            !isAuxiliaryAction &&
            snapshot.activeExecutionIdsByUnit.get(event.unitId) !==
              event.executionId
          ) {
            snapshot.activeExecutionIdsByUnit = new Map(
              snapshot.activeExecutionIdsByUnit,
            );
            snapshot.activeExecutionIdsByUnit.set(
              event.unitId,
              event.executionId,
            );
            changed = true;
          }
        } else if (
          snapshot.activeExecutionIdsByUnit.get(event.unitId) ===
          event.executionId
        ) {
          snapshot.activeExecutionIdsByUnit = new Map(
            snapshot.activeExecutionIdsByUnit,
          );
          snapshot.activeExecutionIdsByUnit.delete(event.unitId);
          changed = true;
        }
      }
      if (
        event.target === "all" &&
        event.status !== "running" &&
        !keepRunAllPending &&
        snapshot.runAllState !== null
      ) {
        snapshot.runAllState = null;
        changed = true;
      }
      break;
    }
    case "event/execution.started": {
      const event = params as ExecutionStartedEvent;
      if (event.unitType !== "definition") {
        cloneUnitMap(event.unitType);
        applyStartedStateForNonDefinitionUnit(snapshot, event);
        changed = true;
      } else {
        const prev = snapshot.executions.get(event.unitId);
        if (!prev || prev.status !== "running") {
          snapshot.executions = new Map(snapshot.executions);
          snapshot.executions.set(event.unitId, {
            unitId: event.unitId,
            status: "running",
            actionName: event.actionName ?? null,
            result: null,
            output: prev?.output ?? null,
            startedAt: Date.now(),
            duration: null,
          });
          changed = true;
        }
      }
      if (snapshot.runAllState?.isRunning && event.progress) {
        snapshot.runAllState = {
          isRunning: true,
          progress: {
            current: event.progress.current,
            total: event.progress.total,
            currentId: event.unitId,
          },
        };
        changed = true;
      }
      break;
    }
    case "event/execution.progress": {
      const event = params as ExecutionProgressEvent;
      if (
        event.unitType === "for" ||
        event.unitType === "content" ||
        event.unitType === "import" ||
        event.unitType === "use"
      ) {
        return;
      }
      const prev = snapshot.executions.get(event.unitId);
      if (prev && prev.status === "running") {
        snapshot.executions = new Map(snapshot.executions);
        snapshot.executions.set(event.unitId, {
          ...prev,
          progress: {
            message: event.detail ?? phaseLabel(event.phase),
            percent: event.percent,
            partialOutput: event.partialOutput,
            updatedAt: Date.now(),
          },
        });
        changed = true;
      }
      break;
    }
    case "event/execution.completed": {
      const event = params as ExecutionCompletedEvent;
      if (event.unitType !== "definition") {
        cloneUnitMap(event.unitType);
        applyCompletedStateForNonDefinitionUnit(snapshot, event);
        changed = true;
      } else {
        const output =
          event.output.unitType === "definition"
            ? (event.output as DefinitionOutput).data
            : (event.output as unknown as PluginOutput);
        const hasError = !!(output as Record<string, unknown>)?.__error;
        if (hasError) {
          logCompletedOutputError(event.unitId, output);
        }
        const prev = snapshot.executions.get(event.unitId);
        snapshot.executions = new Map(snapshot.executions);
        snapshot.executions.set(event.unitId, {
          unitId: event.unitId,
          status: hasError ? "error" : "success",
          actionName: event.meta.actionName ?? null,
          result: hasError
            ? {
                success: false,
                message: (
                  output as Record<string, unknown> & {
                    __error: { message: string };
                  }
                ).__error.message,
              }
            : { success: true, output },
          output,
          startedAt: prev?.startedAt ?? null,
          duration: event.meta.duration,
        });
        changed = true;
      }
      break;
    }
    case "event/execution.failed": {
      const event = params as ExecutionFailedEvent;
      const cancelled = isExecutionCancelledMessage(event.error);
      if (event.unitType !== "definition") {
        cloneUnitMap(event.unitType);
        applyFailedStateForNonDefinitionUnit(snapshot, event, { cancelled });
        changed = true;
      } else {
        const prev = snapshot.executions.get(event.unitId);
        snapshot.executions = new Map(snapshot.executions);
        snapshot.executions.set(event.unitId, {
          unitId: event.unitId,
          status: cancelled ? "idle" : "error",
          actionName: event.actionName ?? null,
          result: cancelled ? null : { success: false, message: event.error },
          output: prev?.output ?? null,
          startedAt: prev?.startedAt ?? null,
          duration: prev?.startedAt ? Date.now() - prev.startedAt : null,
        });
        changed = true;
      }
      break;
    }
    case "event/execution.iterationStarted": {
      const event = params as ExecutionIterationStartedEvent;
      const prev = snapshot.forExecutions.get(event.unitId);
      if (prev) {
        snapshot.forExecutions = new Map(snapshot.forExecutions);
        snapshot.forExecutions.set(event.unitId, {
          ...prev,
          completedIterations: event.index,
        });
        changed = true;
      }
      break;
    }
    case "event/execution.iterationCompleted": {
      const event = params as ExecutionIterationCompletedEvent;
      const prev = snapshot.forExecutions.get(event.unitId);
      if (prev) {
        snapshot.forExecutions = new Map(snapshot.forExecutions);
        const iterations = [...prev.iterations];
        iterations[event.index] = {
          index: event.iteration.index,
          item: event.iteration.item,
          outputs: event.iteration.outputs ?? {},
          definitions: event.iteration.definitions,
          contentDefinitions: event.iteration.contentDefinitions,
          success: event.iteration.success ?? true,
          error: event.iteration.error,
        };
        snapshot.forExecutions.set(event.unitId, {
          ...prev,
          completedIterations: Math.max(
            prev.completedIterations,
            event.index + 1,
          ),
          iterations,
        });
        changed = true;
      }
      break;
    }
    case "event/execution.cascade": {
      const event = params as ExecutionCascadeEvent;
      Object.assign(
        snapshot,
        applyCascadeRunningTargets(snapshot, event.targetUnitIds),
      );
      changed = event.targetUnitIds.length > 0;
      break;
    }
    default:
      return;
  }

  if (!changed) return;

  const nextCache = new Map(sessionExecutionCache);
  nextCache.set(eventSessionId, snapshot);
  store.setState({ sessionExecutionCache: nextCache });
  syncBackgroundRunningTabs();
}

export function routeBackgroundIterationBatch(
  store: ExecutionProjectionStore,
  syncBackgroundRunningTabs: () => void,
  events: readonly ExecutionIterationCompletedEvent[],
): void {
  if (events.length === 0) return;

  const { sessionExecutionCache } = store.getState();
  const nextCache = new Map(sessionExecutionCache);
  const snapshots = new Map<string, ReturnType<typeof getOrCreateSnapshot>>();
  const grouped = new Map<
    string,
    Map<string, ExecutionIterationCompletedEvent[]>
  >();

  for (const event of events) {
    if (!event.sessionId) continue;
    let byUnit = grouped.get(event.sessionId);
    if (!byUnit) {
      byUnit = new Map();
      grouped.set(event.sessionId, byUnit);
    }
    const unitEvents = byUnit.get(event.unitId) ?? [];
    unitEvents.push(event);
    byUnit.set(event.unitId, unitEvents);
  }

  for (const [sessionId, byUnit] of grouped) {
    const existing = nextCache.get(sessionId);
    const snapshot = existing
      ? {
          ...existing,
          forExecutions: new Map(existing.forExecutions),
        }
      : getOrCreateSnapshot(nextCache, sessionId);
    let changed = false;
    for (const [unitId, unitEvents] of byUnit) {
      const prev = snapshot.forExecutions.get(unitId);
      if (!prev) continue;
      const iterations = mergeForIterationEvents(prev.iterations, unitEvents);
      snapshot.forExecutions.set(unitId, {
        ...prev,
        completedIterations: Math.max(
          prev.completedIterations,
          iterations.length,
        ),
        iterations,
      });
      changed = true;
    }
    if (changed) snapshots.set(sessionId, snapshot);
  }

  if (snapshots.size === 0) return;
  for (const [sessionId, snapshot] of snapshots) {
    nextCache.set(sessionId, snapshot);
  }
  store.setState({ sessionExecutionCache: nextCache });
  syncBackgroundRunningTabs();
}
